import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { App as AntApp, ConfigProvider, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useTheme } from './contexts/ThemeContext';
import './pages/PageStyles.css';
import './glassmorphism.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppConfigProvider } from './contexts/AppConfigContext';
import MainLayout from './components/MainLayout';
import DevConsole from './components/DevConsole';
import Dashboard from './pages/Dashboard';
import ApplicationList from './pages/ApplicationList';
import SkillList from './pages/SkillList';
import ToolList from './pages/ToolList';
import MCPServerList from './pages/MCPServerList';
import DocumentList from './pages/DocumentList';
import ExpertList from './pages/ExpertList';
import EmployeeList from './pages/EmployeeList';
import EmployeeDetail from './pages/EmployeeDetail';
import SkillDetail from './pages/SkillDetail';
import ToolDetail from './pages/ToolDetail';
import DocumentDetail from './pages/DocumentDetail';
import ApplicationDetail from './pages/ApplicationDetail';
import ApplicationView from './pages/ApplicationView';
import ApplicationStandalone from './pages/ApplicationStandalone';
import Login from './pages/Login';
import StandaloneLogin from './pages/StandaloneLogin';
import TicketList from './pages/TicketList';
import ProductList from './pages/ProductList';
import AgentTaskList from './pages/AgentTaskList';
import Chat from './pages/Chat';
import SessionManagement from './pages/SessionManagement';
import UserManagement from './pages/UserManagement';
import DictionaryManagement from './pages/DictionaryManagement';
import FeatureConfigManagement from './pages/FeatureConfigManagement';
import HelpConfigManagement from './pages/HelpConfigManagement';
import SystemConfigManagement from './pages/SystemConfigManagement';
import EnvVarsConfig from './pages/EnvVarsConfig';
import AgentConfig from './pages/AgentConfig';
import AgentBaseConfig from './pages/AgentBaseConfig';
import PluginManagement from './pages/PluginManagement';
import RoleManagement from './pages/RoleManagement';
import Profile from './pages/Profile';
import PromptManagement from './pages/PromptManagement';
import UserDetail from './pages/UserDetail';
import Settings from './pages/Settings';
import QuickChat from './components/QuickChat';

import { PermissionProvider } from './contexts/PermissionContext';

// Each employee gets an isolated Chat instance via key; state is persisted in chatStore
const ChatRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Chat key={id ?? 'default'} employeeId={id} />;
};

// Theme-aware ConfigProvider wrapper
const ThemeConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'glass';

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: isDark ? {
          colorBgElevated: 'rgb(15, 20, 40)',
          colorBgContainer: 'rgb(15, 20, 40)',
          colorBgSpotlight: 'rgb(15, 20, 40)',
          colorText: 'rgba(255, 255, 255, 0.92)',
          colorTextSecondary: 'rgba(255, 255, 255, 0.6)',
          colorBorder: 'rgba(255, 255, 255, 0.1)',
          colorBorderSecondary: 'rgba(255, 255, 255, 0.06)',
          colorPrimary: '#818cf8',
        } : undefined,
        components: isDark ? {
          Modal: {
            colorBgElevated: 'rgb(15, 20, 40)',
            colorBgContainer: 'rgb(15, 20, 40)',
            colorText: 'rgba(255, 255, 255, 0.92)',
            colorBorder: 'rgba(255, 255, 255, 0.1)',
          },
          Drawer: {
            colorBgElevated: 'rgb(15, 20, 40)',
            colorBgContainer: 'rgb(15, 20, 40)',
            colorBgSpotlight: 'rgb(15, 20, 40)',
            colorText: 'rgba(255, 255, 255, 0.92)',
          },
          Popover: {
            colorBgElevated: 'rgba(15, 20, 40, 0.97)',
            colorText: 'rgba(255, 255, 255, 0.92)',
          },
          Tooltip: {
            colorBgSpotlight: 'rgba(15, 20, 40, 0.97)',
            colorTextLightSolid: 'rgba(255, 255, 255, 0.92)',
          },
          Dropdown: {
            colorBgElevated: 'rgba(15, 20, 40, 0.95)',
            colorText: 'rgba(255, 255, 255, 0.7)',
          },
          Menu: {
            colorBgContainer: 'rgba(15, 20, 40, 0.95)',
            itemColor: 'rgba(255, 255, 255, 0.7)',
          },
          Select: {
            colorBgElevated: 'rgba(15, 20, 40, 0.97)',
            colorText: 'rgba(255, 255, 255, 0.7)',
          },
          DatePicker: {
            colorBgContainer: 'rgba(15, 20, 40, 0.97)',
            colorText: 'rgba(255, 255, 255, 0.7)',
          },
          Cascader: {
            colorBgElevated: 'rgba(15, 20, 40, 0.97)',
            colorText: 'rgba(255, 255, 255, 0.7)',
          },
          TreeSelect: {
            colorBgElevated: 'rgba(15, 20, 40, 0.97)',
            colorText: 'rgba(255, 255, 255, 0.7)',
          },
        } : undefined,
      }}
    >
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
};

const STANDALONE_APP_ID = process.env.REACT_APP_STANDALONE_APP_ID;

// Handle Tauri new window route redirect
const TauriRouteRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const route = searchParams.get('__route');
  if (route) {
    return <Navigate to={route} replace />;
  }
  return <>{children}</>;
};

const ProtectedLayout: React.FC = () => {
  const isAuthenticated = !!localStorage.getItem('token');
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (STANDALONE_APP_ID) {
    return <Navigate to={`/app/${STANDALONE_APP_ID}/index`} replace />;
  }
  return <MainLayout />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('token');
  const [searchParams] = useSearchParams();
  if (isAuthenticated) {
    if (STANDALONE_APP_ID) {
      return <Navigate to={`/app/${STANDALONE_APP_ID}/index`} replace />;
    }
    const redirect = searchParams.get('redirect');
    return <Navigate to={redirect ? decodeURIComponent(redirect) : '/dashboard'} replace />;
  }
  return <>{children}</>;
};

// Handle navigate event from quick-chat window
const TauriNavigateListener: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!(window as any).__TAURI_INTERNALS__) return;
    let unlisten: (() => void) | undefined;
    import('@tauri-apps/api/event').then(({ listen }) => {
      listen('navigate', (e: any) => {
        navigate(e.payload as string);
      }).then((fn) => { unlisten = fn; });
    });
    return () => { unlisten?.(); };
  }, [navigate]);
  return null;
};

const App: React.FC = () => {
  return (
    <AppConfigProvider>
      <ThemeProvider>
        <ThemeConfigProvider>
          <PermissionProvider>
          <BrowserRouter>
            <TauriRouteRedirect>
            <TauriNavigateListener />
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/applications" element={<ApplicationList />} />
                <Route path="/session-management" element={<SessionManagement />} />
                <Route path="/app/:slug/info" element={<ApplicationDetail />} />
                <Route path="/app/:slug/view" element={<ApplicationView />} />
                <Route path="/skills" element={<SkillList />} />
                <Route path="/skills/:id" element={<SkillDetail />} />
                <Route path="/tools" element={<ToolList />} />
                <Route path="/tools/:id" element={<ToolDetail />} />
                <Route path="/mcp-servers" element={<MCPServerList />} />
                <Route path="/documents" element={<DocumentList />} />
                <Route path="/documents/:id" element={<DocumentDetail />} />
                <Route path="/experts" element={<ExpertList />} />
                <Route path="/employees" element={<EmployeeList />} />
                <Route path="/employees/:id" element={<EmployeeDetail />} />
                <Route path="/tickets" element={<TicketList />} />
                <Route path="/agent-tasks" element={<AgentTaskList />} />
                <Route path="/products" element={<ProductList />} />
                <Route path="/employees/:id/chat" element={<ChatRoute />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/user-management/:id" element={<UserDetail />} />
                <Route path="/dictionary-management" element={<DictionaryManagement />} />
                <Route path="/feature-config" element={<FeatureConfigManagement />} />
                <Route path="/help-config" element={<HelpConfigManagement />} />
                <Route path="/config/agent" element={<AgentConfig />} />
                <Route path="/config/model_provider" element={<AgentBaseConfig />} />
                <Route path="/config/system_base" element={<SystemConfigManagement />} />
                <Route path="/config/env_vars" element={<EnvVarsConfig />} />
                <Route path="/config/plugin" element={<PluginManagement />} />
                <Route path="/prompt-management" element={<PromptManagement />} />
                <Route path="/role-management" element={<RoleManagement />} />
                <Route path="/profile" element={<Profile />} />
              </Route>
              <Route path="/app/:slug/index" element={<ApplicationStandalone />} />
              <Route path="/app/:slug/login" element={<StandaloneLogin />} />
              <Route path="/quick-chat" element={<QuickChat />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </TauriRouteRedirect>
            <DevConsole />
          </BrowserRouter>
          </PermissionProvider>
        </ThemeConfigProvider>
      </ThemeProvider>
    </AppConfigProvider>
  );
};

export default App;
